import { useCallback, useEffect, useState } from "react";
import { useApp } from "../vault/ctx";
import { Modal } from "../vault/Common";
import {
  listMembers,
  listRoles,
  listInvites,
  createInvite,
  revokeInvite,
  updateMember,
  removeMember,
  VAULT_PERMISSIONS,
  type MembersResponse,
  type TeamMember,
  type TeamRole,
  type TeamUserRef,
  type TeamInvite,
} from "../../features/team/teamApi";

// --- populated-vs-id narrowing (controller populates userId/roleId, but the contract types allow strings) ---
const userOf = (m: TeamMember): TeamUserRef | null => (typeof m.userId === "object" ? m.userId : null);
const roleOf = (m: TeamMember): TeamRole | null => (typeof m.roleId === "object" ? m.roleId : null);
const roleIdOf = (m: TeamMember): string => (typeof m.roleId === "object" ? m.roleId._id : m.roleId);
const inviteRoleName = (inv: TeamInvite): string => (typeof inv.roleId === "object" ? inv.roleId.name : "—");

const PERM_LABEL = new Map(VAULT_PERMISSIONS.map((p) => [p.key, p.label]));
function permSummary(perms: string[]): string {
  // '*' is the wildcard the server grants a workspace owner (and the Admin role
  // seeded with a new workspace). It matches no entry in VAULT_PERMISSIONS, so
  // without this the member with the MOST authority was listed as having none.
  if (perms.includes("*")) return "Full access";
  const labels = perms.map((k) => PERM_LABEL.get(k)).filter((l): l is string => Boolean(l));
  return labels.length ? labels.join(", ") : "No management permissions";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((p) => p[0]).join("") || "?").toUpperCase();
}

const AVATAR_COLORS = ["#2F4B39", "#5B4E93", "#8F4E3A", "#3A6E8F", "#7A3A6E", "#4E7A3A"];
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

// Small dropdown that fits inside a table cell (reference has no per-row select, so token-styled inline).
const cellSelect: React.CSSProperties = {
  border: "1px solid var(--border)", borderRadius: 8, height: 30, padding: "0 8px", background: "#fff",
};

export default function TeamPage() {
  const { open, close } = useApp();
  const [data, setData] = useState<MembersResponse | null>(null);
  const [roles, setRoles] = useState<TeamRole[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(
    () =>
      Promise.all([listMembers(), listRoles(), listInvites()])
        .then(([m, r, i]) => { setData(m); setRoles(r); setInvites(i); setError(null); })
        .catch((e) => setError(e instanceof Error ? e.message : "Could not load team"))
        .finally(() => setLoading(false)),
    [],
  );

  useEffect(() => { void refresh(); }, [refresh]);

  const changeRole = async (id: string, roleId: string) => {
    try { await updateMember(id, { roleId }); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not update role"); }
  };

  const remove = async (m: TeamMember) => {
    if (!confirm(`Remove ${userOf(m)?.name ?? "this member"} from the workspace?`)) return;
    try { await removeMember(m._id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not remove member"); }
  };

  const revoke = async (id: string) => {
    try { await revokeInvite(id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not revoke invite"); }
  };

  const seats = data?.seats;
  const subtitle = seats
    ? `${seats.memberCount} of ${seats.maxMembers} seats used${seats.planName ? ` on the ${seats.planName} plan` : ""}.` +
      (seats.pendingInvites ? ` ${seats.pendingInvites} invite${seats.pendingInvites > 1 ? "s" : ""} pending.` : "")
    : "Loading…";

  const members = data?.members ?? [];

  return (<>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1 }}><div className="page-title">Team</div><div className="page-sub">{subtitle}</div></div>
      <button className="btn primary" onClick={() => open("teamInvite")}>+ Invite member</button>
    </div>

    {error && <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--red)" }}>{error}</div>}

    <table className="list-table on" style={{ marginTop: 16 }}>
      <thead><tr><th style={{ width: '34%' }}>Member</th><th>Role</th><th>Permissions</th><th>Joined</th><th></th></tr></thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={5} style={{ color: "var(--muted)" }}>Loading team…</td></tr>
        ) : members.length === 0 ? (
          <tr><td colSpan={5} style={{ color: "var(--muted)" }}>No team members yet.</td></tr>
        ) : members.map((m) => {
          const u = userOf(m);
          const isOwner = !!u && u._id === data?.ownerId;
          return (
            <tr key={m._id}>
              <td className="nm">
                <span className="avatar" style={{ width: 28, height: 28, fontSize: 11, background: avatarColor(u?._id ?? m._id) }}>{initials(u?.name ?? u?.email ?? "?")}</span>
                {u?.name ?? u?.email ?? "Unknown"}
                {isOwner && <span className="badge gold">Owner</span>}
              </td>
              <td>
                {isOwner
                  ? <span className="badge gray">{roleOf(m)?.name ?? "—"}</span>
                  : <select style={cellSelect} value={roleIdOf(m)} onChange={(e) => changeRole(m._id, e.target.value)}>
                      {roles.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                    </select>}
              </td>
              <td>{permSummary(roleOf(m)?.permissions ?? [])}</td>
              <td>{fmtDate(m.joinedAt)}</td>
              <td>{!isOwner && <button className="btn ghost" onClick={() => remove(m)}>Remove</button>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {invites.length > 0 && (
      <>
        <div className="page-sub" style={{ marginTop: 26, marginBottom: 0, fontWeight: 700, color: "var(--text)" }}>Pending invites</div>
        <table className="list-table on" style={{ marginTop: 12 }}>
          <thead><tr><th style={{ width: '34%' }}>Email</th><th>Role</th><th>Invited</th><th></th></tr></thead>
          <tbody>
            {invites.map((inv) => (
              <tr key={inv._id}>
                <td className="nm">{inv.email}</td>
                <td><span className="badge gray">{inviteRoleName(inv)}</span></td>
                <td>{fmtDate(inv.createdAt)}</td>
                <td><button className="btn ghost" onClick={() => revoke(inv._id)}>Revoke</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}

    <InviteModal roles={roles} onCreated={() => { void refresh(); }} onClose={() => close("teamInvite")} />
  </>);
}

function InviteModal({ roles, onCreated, onClose }: { roles: TeamRole[]; onCreated: () => void; onClose: () => void }) {
  const { modals } = useApp();
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!roleId && roles.length) setRoleId((roles.find((r) => r.isDefault) ?? roles[0])._id);
  }, [roles, roleId]);

  // Modal (Common.Modal) unmounts only its DOM on close, not this component — so a
  // failed-invite error (or a stuck busy flag) would linger on reopen. Clear on close.
  useEffect(() => {
    if (!modals.teamInvite) { setErr(null); setBusy(false); }
  }, [modals.teamInvite]);

  const submit = async () => {
    if (!email.trim() || !roleId) { setErr("Email and role are required."); return; }
    setBusy(true); setErr(null);
    try {
      await createInvite(email.trim(), roleId);
      setEmail("");
      onClose();
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not send invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal id="teamInvite" title="Invite member" maxWidth={460}>
      <div className="field">
        <label>Email address</label>
        <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@studio.com" />
      </div>
      <div className="field">
        <label>Role</label>
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          {roles.length === 0 && <option value="">No roles yet</option>}
          {roles.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
        </select>
      </div>
      {err && <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--red)" }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn primary" onClick={submit} disabled={busy || roles.length === 0}>{busy ? "Sending…" : "Send invite"}</button>
      </div>
    </Modal>
  );
}
